import React from 'react';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { CodeBlockRenderer } from './CodeBlockRenderer';
import type { ChatMessage } from './types';

interface ChatMessageListProps {
  messages: ChatMessage[];
  isDark: boolean;
  colors: ColorTheme;
  bottomRef: React.Ref<HTMLDivElement>;
  isStreaming: boolean;
  indicator: React.ReactNode;
}

/**
 * Scrollable message list for the deploy chat. Renders each message in a
 * bubble and keeps a bottom-anchor ref so the parent can auto-scroll on new
 * message arrival.
 */
export function ChatMessageList(props: ChatMessageListProps): React.JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        borderRadius: '10px',
        border: `1px solid ${props.colors.border}`,
        backgroundColor: props.isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
      }}
    >
      {props.messages.map((msg, i) => (
        <MessageBubble key={i} message={msg} isDark={props.isDark} colors={props.colors} />
      ))}
      {props.isStreaming && props.indicator}
      <div ref={props.bottomRef} />
    </div>
  );
}

/** One chat message bubble. */
function MessageBubble({
  message, isDark, colors,
}: {
  message: ChatMessage;
  isDark: boolean;
  colors: ColorTheme;
}): React.JSX.Element {
  const isUser = message.role === 'user';
  const bg = pickBackground(message, isDark, isUser);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '10px',
      }}
    >
      <div
        style={{
          maxWidth: '85%',
          padding: '10px 14px',
          borderRadius: '10px',
          backgroundColor: bg,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: '13px',
            color: colors.text,
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <CodeBlockRenderer content={message.content} />
        </div>
      </div>
    </div>
  );
}

/** Pick the bubble background based on message role + type. */
function pickBackground(message: ChatMessage, isDark: boolean, isUser: boolean): string {
  if (message.type === 'error') return isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.08)';
  if (message.type === 'success') return isDark ? 'rgba(74,222,128,0.1)' : 'rgba(74,222,128,0.08)';
  if (message.type === 'progress') return isDark ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.08)';
  if (isUser) return isDark ? 'rgba(1,169,237,0.15)' : 'rgba(1,169,237,0.1)';
  return isDark ? COLORS.dark.card : COLORS.light.card;
}
